import { Link } from 'react-router';
import { ROUTES } from '@/app/router/routes';
import { useEffect, useId, useRef, useState } from 'react';

import { ChevronDownIcon, LogOutIcon, UsersIcon } from '@/components/icons';
import type { AuthUser } from '@/features/auth/types';
import { cn } from '@/lib/utils/cn';

const ROLE_LABELS: Record<AuthUser['role'], string> = {
  ADMIN: 'Administrateur',
  RECRUITER: 'Recruteur',
  HIRING_MANAGER: 'Manager',
  VIEWER: 'Lecteur',
  CANDIDATE: 'Consultant',
};

/** Les initiales restent disponibles tant que l'API ne fournit pas de photo. */
const initialsOf = (user: AuthUser): string => {
  const fromName = (user.firstName.trim().at(0) ?? '') + (user.lastName.trim().at(0) ?? '');
  return (fromName || user.email.slice(0, 2)).toUpperCase();
};

const AVATAR_STYLE =
  'grid shrink-0 place-items-center rounded-full border border-navy-900/10 ' +
  'bg-navy-900 bg-linear-to-b from-navy-700 to-navy-900 font-semibold tracking-normal text-white ' +
  'shadow-[inset_0_1px_0_0_rgb(255_255_255/0.2),0_2px_4px_-1px_rgb(15_42_95/0.18)]';

interface UserMenuProps {
  user: AuthUser;
  onLogout: () => void;
}

/** Accès au compte et menu de déconnexion, utilisables au clavier et au toucher. */
export const UserMenu = ({ user, onLogout }: UserMenuProps) => {
  const [open, setOpen] = useState(false);
  const menuId = useId();
  const triggerId = menuId + '-trigger';
  const containerRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const logoutRef = useRef<HTMLButtonElement>(null);
  const accountRef = useRef<HTMLAnchorElement>(null);
  const initialFocus = useRef<'first' | 'last'>('first');
  const firstName = user.firstName.trim();
  const lastName = user.lastName.trim();
  const fullName = [firstName, lastName].filter(Boolean).join(' ') || user.email;
  const shortName = firstName
    ? firstName + (lastName ? ' ' + lastName.at(0) + '.' : '')
    : lastName || user.email;

  useEffect(() => {
    if (!open) return;
    (initialFocus.current === 'last' ? logoutRef.current : accountRef.current)?.focus();

    const handlePointerDown = (event: PointerEvent) => {
      if (event.target instanceof Node && !containerRef.current?.contains(event.target)) {
        setOpen(false);
      }
    };

    document.addEventListener('pointerdown', handlePointerDown);
    return () => document.removeEventListener('pointerdown', handlePointerDown);
  }, [open]);

  const closeAndFocusTrigger = () => {
    setOpen(false);
    triggerRef.current?.focus();
  };

  return (
    <div
      ref={containerRef}
      className="relative shrink-0"
      onBlur={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget)) setOpen(false);
      }}
      onKeyDown={(event) => {
        if (open && event.key === 'Escape') {
          event.preventDefault();
          event.stopPropagation();
          closeAndFocusTrigger();
        }
      }}
    >
      <button
        id={triggerId}
        ref={triggerRef}
        type="button"
        onClick={() => {
          initialFocus.current = 'first';
          setOpen((current) => !current);
        }}
        onKeyDown={(event) => {
          if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
            event.preventDefault();
            initialFocus.current = event.key === 'ArrowUp' ? 'last' : 'first';
            if (open)
              (initialFocus.current === 'last' ? logoutRef.current : accountRef.current)?.focus();
            else setOpen(true);
          }
        }}
        aria-label={'Menu du compte de ' + fullName + ', ' + ROLE_LABELS[user.role]}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={open ? menuId : undefined}
        className={cn(
          'flex min-h-12 cursor-pointer items-center gap-2.5 rounded-[10px] border p-1.5 pr-2.5',
          'transition-[background-color,border-color,box-shadow] duration-200 motion-reduce:transition-none',
          'focus-visible:ring-accent-700/25 focus-visible:ring-4 focus-visible:ring-offset-2 focus-visible:outline-none',
          open
            ? 'border-navy-200/80 bg-navy-50/80 bg-linear-to-b from-white/80 to-transparent shadow-[inset_0_1px_0_0_rgb(255_255_255/0.95),0_2px_4px_-1px_rgb(15_42_95/0.08)]'
            : 'border-transparent hover:border-navy-100 hover:bg-navy-50/60',
        )}
      >
        <span aria-hidden className={cn(AVATAR_STYLE, 'size-9 text-xs')}>
          {user.avatarUrl ? (
            <img src={user.avatarUrl} alt="" className="size-full rounded-full object-cover" />
          ) : (
            initialsOf(user)
          )}
        </span>
        <span className="hidden min-w-0 text-left sm:block">
          <span className="text-navy-900 block max-w-36 truncate text-[15px] leading-5 font-semibold">
            {shortName}
          </span>
          <span className="text-ink-500 block text-xs leading-5">{ROLE_LABELS[user.role]}</span>
        </span>
        <ChevronDownIcon
          className={cn('ml-1 shrink-0 text-xs', open ? 'text-accent-700' : 'text-navy-400')}
        />
      </button>

      {open ? (
        <div className="border-navy-200/80 absolute right-0 z-50 mt-2.5 w-72 max-w-[calc(100vw-3rem)] overflow-hidden rounded-[10px] border bg-white shadow-[0_12px_32px_-12px_rgb(15_42_95/0.24),0_2px_6px_-2px_rgb(15_42_95/0.08)]">
          <div className="border-navy-100 bg-navy-50/70 border-b bg-linear-to-b from-white/80 to-transparent p-4 shadow-[inset_0_1px_0_0_rgb(255_255_255/0.95)]">
            <p className="text-ink-500 mb-3 text-xs font-medium">Mon compte</p>
            <div className="flex items-center gap-3">
              <span aria-hidden className={cn(AVATAR_STYLE, 'size-10 text-sm')}>
                {user.avatarUrl ? (
                  <img
                    src={user.avatarUrl}
                    alt=""
                    className="size-full rounded-full object-cover"
                  />
                ) : (
                  initialsOf(user)
                )}
              </span>
              <div className="min-w-0 flex-1">
                <p className="text-navy-900 text-sm leading-5 font-semibold wrap-anywhere">
                  {fullName}
                </p>
                <p className="text-ink-500 mt-0.5 text-xs leading-5 wrap-anywhere">{user.email}</p>
              </div>
            </div>
            <span className="border-accent-200/60 bg-accent-50 text-accent-800 mt-3 inline-flex rounded-md border px-2 py-0.5 text-[11px] leading-4 font-medium shadow-[inset_0_1px_0_0_rgb(255_255_255/0.8)]">
              {ROLE_LABELS[user.role]}
            </span>
          </div>

          <div
            id={menuId}
            role="menu"
            aria-labelledby={triggerId}
            className="p-1.5"
            onKeyDown={(event) => {
              const items = [accountRef.current, logoutRef.current];
              if (['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(event.key)) {
                event.preventDefault();
                const current = items.indexOf(
                  document.activeElement as HTMLAnchorElement | HTMLButtonElement,
                );
                const next = event.key === 'Home' ? 0 : event.key === 'End' ? 1 : (current + 1) % 2;
                items[next]?.focus();
              }
              if (event.key === 'Tab' && event.shiftKey) {
                event.preventDefault();
                closeAndFocusTrigger();
              }
            }}
          >
            <Link
              ref={accountRef}
              to={ROUTES.account}
              role="menuitem"
              tabIndex={-1}
              onClick={() => setOpen(false)}
              className="text-navy-800 hover:border-navy-100 hover:bg-navy-50 focus-visible:bg-accent-50 focus-visible:outline-none flex min-h-11 items-center gap-3 rounded-lg border border-transparent px-2 py-2 text-sm font-medium transition-colors motion-reduce:transition-none"
            >
              <span className="bg-navy-50 text-navy-500 grid size-8 shrink-0 place-items-center rounded-md text-base">
                <UsersIcon />
              </span>
              Gérer mon compte
            </Link>
            <button
              ref={logoutRef}
              type="button"
              role="menuitem"
              tabIndex={-1}
              onClick={() => {
                closeAndFocusTrigger();
                onLogout();
              }}
              className={cn(
                'group/logout flex min-h-11 w-full cursor-pointer items-center gap-3 rounded-lg border border-transparent px-2 py-2 text-left text-sm font-medium',
                'text-ink-700 transition-[background-color,border-color,color] duration-150 motion-reduce:transition-none',
                'hover:border-red-100 hover:bg-red-50/70 hover:text-red-700',
                'focus-visible:border-red-200 focus-visible:bg-red-50/70 focus-visible:text-red-700 focus-visible:outline-none',
              )}
            >
              <span className="bg-navy-50 text-navy-500 grid size-8 shrink-0 place-items-center rounded-md text-base transition-colors group-hover/logout:bg-red-100/60 group-hover/logout:text-red-600 group-focus-visible/logout:bg-red-100/60 group-focus-visible/logout:text-red-600 motion-reduce:transition-none">
                <LogOutIcon />
              </span>
              Se déconnecter
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
};
